import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const MonthlyBills = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [propertyFilter, setPropertyFilter] = useState('all');

  const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/expenses/monthly-bills-summary?year=${year}&month=${month}`, { headers });
      if (res.data.success) setData(res.data.data);
    } catch (e) {
      toast.error('Failed to load monthly bills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year, month]);

  const fmt = (n) => `$${(parseFloat(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const properties = data?.properties || [];
  const filtered = propertyFilter === 'all'
    ? properties
    : properties.filter(p => String(p.propertyId) === propertyFilter);

  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Monthly Bills & Budget</h1>
      <p className="text-gray-600 mb-6">Personal cash-flow by house — separate from Tax Forms. Variable bills show last paid amounts from your rules.</p>

      <div className="flex flex-wrap gap-4 mb-6 items-end">
        <label className="text-sm">
          Month
          <select value={month} onChange={e => setMonth(parseInt(e.target.value, 10))} className="ml-2 border rounded px-2 py-1">
            {monthNames.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
        <label className="text-sm">
          Year
          <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value, 10))} className="ml-2 border rounded px-2 py-1 w-24" />
        </label>
        <label className="text-sm">
          Property
          <select value={propertyFilter} onChange={e => setPropertyFilter(e.target.value)} className="ml-2 border rounded px-2 py-1">
            <option value="all">All properties</option>
            {properties.map(p => (
              <option key={p.propertyId} value={p.propertyId}>{p.propertyName}</option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : data ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
              <div className="text-sm text-gray-500">Personal spend (month)</div>
              <div className="text-xl font-semibold">{fmt(data.totals.personalSpend)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
              <div className="text-sm text-gray-500">Tax-deductible (month)</div>
              <div className="text-xl font-semibold">{fmt(data.totals.taxableSpend)}</div>
              <div className="text-xs text-gray-400">Also on Tax Forms</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-emerald-500">
              <div className="text-sm text-gray-500">Rental income (month)</div>
              <div className="text-xl font-semibold">{fmt(data.totals.rentalIncome)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-500">
              <div className="text-sm text-gray-500">Est. recurring obligations</div>
              <div className="text-xl font-semibold">{fmt(data.totals.estimatedMonthlyObligations)}</div>
            </div>
          </div>

          {filtered.map(prop => (
            <div key={prop.propertyId || 'u'} className="bg-white rounded-lg shadow mb-6 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b flex justify-between items-center">
                <h2 className="font-semibold text-lg">{prop.propertyName}</h2>
                <span className="text-sm text-gray-600">
                  Personal {fmt(prop.personalSpend)} · Tax {fmt(prop.taxableSpend)}
                  {prop.rentalIncome > 0 && ` · Rent in ${fmt(prop.rentalIncome)}`}
                </span>
              </div>
              {prop.recurringTemplates?.length > 0 && (
                <div className="px-4 py-3 border-b">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Recurring bills (variable amounts OK)</h3>
                  <ul className="space-y-1 text-sm">
                    {prop.recurringTemplates.map(r => (
                      <li key={r.id} className="flex justify-between">
                        <span>{r.description} <span className="text-gray-400">({r.category})</span></span>
                        <span className="font-medium">{fmt(r.amount)}/mo</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="px-4 py-3">
                <h3 className="text-sm font-medium text-gray-700 mb-2">This month&apos;s transactions</h3>
                {prop.bills?.length ? (
                  <ul className="divide-y text-sm">
                    {prop.bills.map(b => (
                      <li key={b.id} className="py-2 flex justify-between gap-4">
                        <span className="truncate">{b.date?.slice?.(0, 10) || b.date} — {b.description}</span>
                        <span className={b.is_deductible ? 'text-green-700' : 'text-blue-700'}>
                          {fmt(b.amount)} {b.is_deductible ? '(tax)' : '(personal)'}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-400 text-sm">No transactions this month.</p>
                )}
              </div>
            </div>
          ))}

          {data.rules?.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4 mt-6">
              <h3 className="font-medium mb-2">Saved payee rules ({data.rules.length})</h3>
              <p className="text-xs text-gray-500 mb-3">Amounts can change each month — rules match by payee, not dollar amount.</p>
              <ul className="text-sm grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.rules.filter(r => r.disposition !== 'transfer_ignore').slice(0, 20).map(r => (
                  <li key={r.match_key} className="border rounded px-2 py-1">
                    <strong>{r.display_name || r.match_key}</strong>
                    {' — '}{r.taxable ? r.category : `personal: ${r.budget_category || r.category}`}
                    {r.avg_amount != null && ` (~${fmt(r.avg_amount)}, last ${fmt(r.last_amount)})`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default MonthlyBills;
